import { useMemo, useState } from 'react';
import { CalendarPlus, X } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import ExtendVigenciaModal from './ExtendVigenciaModal';
import { formatDateLabel, formatDaysRemaining, summarizeSubscription } from '../../utils/organizationLicenseSummary';

const CONSUMER_ROLES = new Set(['usuario_final', 'usuario']);

function isEndUser(u) {
  const roles = [u?.rol, ...(Array.isArray(u?.roles) ? u.roles : [])].filter(Boolean);
  return roles.some((r) => CONSUMER_ROLES.has(r));
}

export default function OrgEndUsersVigenciasModal({
  open,
  gym,
  users = [],
  onClose,
  onExtend,
  extending = false,
}) {
  const { t } = useI18n();
  const [extendTarget, setExtendTarget] = useState(null);

  const endUsers = useMemo(() => {
    if (!gym?.id) return [];
    return users.filter((u) => {
      if (!isEndUser(u)) return false;
      return Number(u.gym_id || u.gymId) === Number(gym.id);
    });
  }, [users, gym?.id]);

  if (!open || !gym) return null;

  const handleExtendConfirm = async (days) => {
    if (!extendTarget) return;
    await onExtend?.({
      userId: extendTarget.id,
      userName: extendTarget.nombre,
      planName: extendTarget.subscription?.plan || extendTarget.planId,
      moduleCode: 'd28d',
      days,
    });
    setExtendTarget(null);
  };

  return (
    <>
      <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={onClose}>
        <div className="form-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="form-modal-header">
            <div className="min-w-0">
              <h3 className="font-bold truncate">
                {t('vigencias.end_users_title', 'Vigencias de usuarios')}: {gym.nombre || gym.brand_name}
              </h3>
              <p className="text-xs text-stone-500 mt-0.5">
                {t('vigencias.end_users_hint', 'Usuarios finales asociados a esta sede o gimnasio.')}
              </p>
            </div>
            <button type="button" className="form-close-btn shrink-0" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          <div className="form-modal-content">
            {endUsers.length === 0 ? (
              <p className="text-sm text-stone-500">{t('vigencias.no_end_users', 'Sin usuarios finales en esta sede.')}</p>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.user', 'Usuario')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.plan', 'Plan')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('vigencias.valid_until', 'Vigencia')}</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-stone-500 uppercase">{t('common.actions', 'Acciones')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {endUsers.map((u) => {
                      const sub = summarizeSubscription(u.subscription);
                      return (
                        <tr key={u.id}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-stone-900">{u.nombre}</div>
                            <div className="text-xs text-stone-500">{u.email}</div>
                          </td>
                          <td className="px-3 py-2 text-stone-700">{sub.plan || '—'}</td>
                          <td className="px-3 py-2 text-stone-700">
                            {sub.validUntil ? (
                              <>
                                {formatDateLabel(sub.validUntil)}
                                <span className="text-xs text-stone-500 ml-1">({formatDaysRemaining(sub.daysLeft)})</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {u.subscription?.has_account && u.subscription?.active ? (
                              <button
                                type="button"
                                className="btn-secondary text-xs inline-flex items-center gap-1"
                                onClick={() => setExtendTarget(u)}
                              >
                                <CalendarPlus className="w-3 h-3" />
                                {t('vigencias.extend_short', 'Ampliar')}
                              </button>
                            ) : (
                              <span className="text-xs text-stone-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <ExtendVigenciaModal
        open={Boolean(extendTarget)}
        userName={extendTarget?.nombre || ''}
        planName={extendTarget?.subscription?.plan || extendTarget?.planId || ''}
        moduleLabel="D28D"
        saving={extending}
        onClose={() => !extending && setExtendTarget(null)}
        onConfirm={handleExtendConfirm}
      />
    </>
  );
}
